import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';

/**
 * ProtectedRoute component - wraps routes that require authentication and/or specific roles
 *
 * @param {Object} props
 * @param {React.ReactNode} props.children - Child components to render if authorized
 * @param {boolean} props.requireEmployee - If true, only employees can access this route
 * @param {boolean} props.requireCustomer - If true, only customers can access this route
 * @param {string} props.redirectTo - Path to redirect to if unauthorized (default: '/login')
 * @returns {React.ReactNode}
 */
const ProtectedRoute = ({
  children,
  requireEmployee = false,
  requireCustomer = false,
  redirectTo = '/login',
}) => {
  const { isAuthenticated, isEmployee, isCustomer, loading } = useAuth();
  const location = useLocation();

  // Show nothing while auth state is loading
  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <p>Loading...</p>
      </div>
    );
  }

  // Redirect to login if not authenticated, preserving the intended destination
  if (!isAuthenticated) {
    const currentPath = location.pathname + location.search;
    const loginUrl = `${redirectTo}?next=${encodeURIComponent(currentPath)}`;
    return <Navigate to={loginUrl} replace />;
  }

  // Check role-based access
  // NOTE: These redirects assume exactly two roles (customer / employee).
  // If a third role is added (e.g. admin), update to a role-aware lookup.
  if (requireEmployee && !isEmployee) {
    return <Navigate to="/dashboard" replace />;
  }

  if (requireCustomer && !isCustomer) {
    return <Navigate to="/employee/dashboard" replace />;
  }

  // Render children if authorized
  return children;
};

export default ProtectedRoute;
